import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Link2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getLedger, verifyLedger } from "@/lib/api";
import { TIPO_LABEL, isVisibleTipo, truncateHash, formatDate, formatResultado } from "@/lib/format";
import { CopyButton } from "@/components/CopyButton";

export default function Ledger() {
  const [entries, setEntries] = useState([]);
  const [chain, setChain] = useState(null);

  useEffect(() => {
    getLedger({ limit: 200 }).then((d) => setEntries(d.filter((e) => isVisibleTipo(e.record?.tipo)))).catch(() => {});
    verifyLedger().then(setChain).catch(() => {});
  }, []);

  return (
    <div data-theme="institutional">
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary"><Link2 className="h-4 w-4" /> Bitácora inmutable</span>
          <h1 className="mt-1 font-display text-3xl font-bold">Registro encadenado de sorteos</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">Cada sorteo se enlaza al anterior mediante su hash SHA-256. Cualquier alteración rompe la cadena y es detectable.</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
        {chain && (
          <Alert className={chain.integra ? "border-success/40 bg-emerald-50" : "border-destructive/40 bg-red-50"} data-testid="ledger-integrity-banner">
            {chain.integra ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4 text-destructive" />}
            <AlertTitle data-testid="ledger-integrity-badge">{chain.integra ? "Cadena íntegra ✓" : `Cadena comprometida (roto en índice ${chain.roto_en_indice})`}</AlertTitle>
            <AlertDescription className="text-xs">{chain.total_registros} registro(s) verificados.</AlertDescription>
          </Alert>
        )}

        <Card className="mt-6">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Juego</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead>Hash del registro</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Aún no hay registros en la bitácora.</TableCell></TableRow>
                  ) : entries.map((e) => (
                    <TableRow key={e.id} data-testid={`ledger-row-${e.index}`}>
                      <TableCell className="font-mono">{e.index}</TableCell>
                      <TableCell>{TIPO_LABEL[e.record.tipo] || e.record.tipo}</TableCell>
                      <TableCell className="font-medium">{formatResultado(e.record.tipo, e.record.resultado)}</TableCell>
                      <TableCell><span className="font-mono text-xs">{truncateHash(e.record_hash)}</span><CopyButton value={e.record_hash} label="" /></TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(e.record.executed_at)}</TableCell>
                      <TableCell><Link to={`/sorteo/${e.record.draw_id}`} className="text-sm text-primary hover:underline">Detalle</Link></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
